import { useEffect, useRef, useState } from "react";

interface CaptchaWidgetProps {
  siteKey?: string;
  onVerify?: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact";
}

/**
 * Cloudflare Turnstile CAPTCHA Widget Component
 *
 * Uses Managed mode (checkbox) with client-side validation
 * Blocks form submission if widget fails to load or validate
 */
export function CaptchaWidget({
  siteKey = import.meta.env.TURNSTILE_SITE_KEY,
  onVerify,
  onError,
  onExpire,
  theme = "auto",
  size = "normal",
}: CaptchaWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const widgetIdRef = useRef<string | null>(null);
  const renderAttemptedRef = useRef(false);

  // Load Turnstile script
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Check if script already loaded
    if (window.turnstile) {
      setIsLoaded(true);
      return;
    }

    // Load Turnstile script
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log("Turnstile: Script loaded successfully");
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.error("Turnstile script failed to load from:", script.src);
      setIsError(true);
      onError?.();
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  // Mark container as ready when it's attached to DOM
  useEffect(() => {
    if (containerRef.current && !isReady) {
      setIsReady(true);
    }
  });

  // Render widget when both script loaded and container ready
  useEffect(() => {
    if (!isLoaded || !isReady || renderAttemptedRef.current) {
      return;
    }

    // Small delay to ensure DOM is fully settled
    const timer = setTimeout(() => {
      renderWidget();
      renderAttemptedRef.current = true;
    }, 100);

    return () => clearTimeout(timer);
  }, [isLoaded, isReady, siteKey, theme, size]);

  // Render Turnstile widget
  const renderWidget = () => {
    if (!containerRef.current) {
      console.error("Turnstile: Container ref not available");
      setIsError(true);
      return;
    }
    if (!window.turnstile) {
      console.error("Turnstile: window.turnstile not available");
      setIsError(true);
      return;
    }
    if (!siteKey) {
      console.error("Turnstile: Site key not available");
      setIsError(true);
      return;
    }

    // Clear any existing widget
    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
    }

    try {
      console.log("Turnstile: Rendering widget with site key:", siteKey);
      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        callback: (token: string) => {
          console.log("Turnstile: Verification successful");
          setIsVerified(true);
          setIsError(false);
          onVerify?.(token);
        },
        "error-callback": () => {
          console.error("Turnstile: Widget error callback triggered");
          setIsVerified(false);
          setIsError(true);
          onError?.();
        },
        "expired-callback": () => {
          console.warn("Turnstile: Widget token expired");
          setIsVerified(false);
          onExpire?.();
        },
      });

      widgetIdRef.current = widgetId;
      console.log("Turnstile: Widget rendered successfully, ID:", widgetId);
    } catch (err) {
      console.error("Turnstile render error:", err);
      setIsError(true);
      onError?.();
    }
  };

  // Reset widget
  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setIsVerified(false);
      setIsError(false);
    }
  };

  // Expose reset method to parent
  useEffect(() => {
    if (containerRef.current) {
      (containerRef.current as any).reset = reset;
    }
  }, [reset]);

  if (isError) {
    return (
      <div className="text-red-400 text-sm">
        ⚠️ CAPTCHA failed to load. Please refresh the page and try again.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-4 bg-gray-800 rounded-lg">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
        <span className="ml-2 text-gray-400 text-sm">Loading CAPTCHA...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div ref={containerRef} className="captcha-container" />
      {!isVerified && (
        <p className="text-xs text-gray-500 mt-2">
          ✓ Complete the CAPTCHA to continue
        </p>
      )}
    </div>
  );
}

// Extend Window interface for Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        params: {
          sitekey: string;
          theme?: string;
          size?: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export default CaptchaWidget;
